import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Plus, Trash2, Palette, Key, Save, Users, Clock, Target, TrendingUp, CheckCircle, Play } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import RocketlaneAPI from '../services/rocketlaneApi';

// Create an instance of RocketlaneAPI
const rocketlaneAPI = new RocketlaneAPI();

const Settings = () => {
  const { handleEnvironmentChange, currentEnvironment } = useDashboard();
  const [themeColors, setThemeColors] = useState({
    primary: '#348AA7',
    secondary: '#08D3A0',
    accent: '#FD745E',
    background: '#f8fafc',
    headerGradient1: '#274768',
    headerGradient2: '#348AA7'
  });
  const [workWeekCapacity, setWorkWeekCapacity] = useState(40);
  const [userCapacities, setUserCapacities] = useState({});
  const [baselineSettings, setBaselineSettings] = useState({
    timeToValue: 30, // days
    taskCompletion: 85, // percentage
    teamUtilization: 80, // percentage
    billableTimeTarget: 75, // percentage
    projectSuccessRate: 90, // percentage
    utilizationThreshold: 10 // percentage - exclude users below this from average
  });

  // Load saved settings from localStorage on component mount
  useEffect(() => {
    const savedThemeColors = localStorage.getItem('rocketlane-theme-colors');
    const savedWorkWeekCapacity = localStorage.getItem('rocketlane-work-week-capacity');
    const savedUserCapacities = localStorage.getItem('rocketlane-user-capacities');
    const savedBaselineSettings = localStorage.getItem('rocketlane-baseline-settings');
    
    if (savedThemeColors) {
      setThemeColors(JSON.parse(savedThemeColors));
    }
    
    if (savedWorkWeekCapacity) {
      setWorkWeekCapacity(JSON.parse(savedWorkWeekCapacity));
    }
    
    if (savedUserCapacities) {
      setUserCapacities(JSON.parse(savedUserCapacities));
    }
    
    if (savedBaselineSettings) {
      setBaselineSettings(JSON.parse(savedBaselineSettings));
    }
  }, []);

  // Activate environment
  const activateEnvironment = (environment) => {
    handleEnvironmentChange(environment);
  };

  // Save theme colors to localStorage and apply them
  const saveThemeColors = (colors) => {
    localStorage.setItem('rocketlane-theme-colors', JSON.stringify(colors));
    setThemeColors(colors);
    
    // Apply theme colors to CSS custom properties
    const root = document.documentElement;
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--secondary-color', colors.secondary);
    root.style.setProperty('--accent-color', colors.accent);
    root.style.setProperty('--background-color', colors.background);
    root.style.setProperty('--header-gradient-1', colors.headerGradient1);
    root.style.setProperty('--header-gradient-2', colors.headerGradient2);
  };



  // Update theme color
  const updateThemeColor = (colorName, value) => {
    const updatedColors = { ...themeColors, [colorName]: value };
    saveThemeColors(updatedColors);
  };

  // Save work week capacity
  const saveWorkWeekCapacity = (capacity) => {
    localStorage.setItem('rocketlane-work-week-capacity', JSON.stringify(capacity));
    setWorkWeekCapacity(capacity);
  };

  // Save user capacities
  const saveUserCapacities = (capacities) => {
    localStorage.setItem('rocketlane-user-capacities', JSON.stringify(capacities));
    setUserCapacities(capacities);
  };

  // Update user capacity
  const updateUserCapacity = (userId, capacity) => {
    const updatedCapacities = { ...userCapacities, [userId]: capacity };
    saveUserCapacities(updatedCapacities);
  };

  // Save baseline settings
  const saveBaselineSettings = (settings) => {
    localStorage.setItem('rocketlane-baseline-settings', JSON.stringify(settings));
    setBaselineSettings(settings);
  };

  // Update baseline setting
  const updateBaselineSetting = (key, value) => {
    const updatedSettings = { ...baselineSettings, [key]: value };
    saveBaselineSettings(updatedSettings);
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <SettingsIcon size={24} />
        <h2>Settings</h2>
      </div>

      <div className="settings-layout">

        {/* Theme Colors Section */}
        <div className="settings-section">
          <div className="section-header">
            <Palette size={20} />
            <h3>Theme Colors</h3>
          </div>
          
          <div className="theme-colors-grid">
            <div className="color-picker-item">
              <label>Primary Color</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={themeColors.primary}
                  onChange={(e) => updateThemeColor('primary', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={themeColors.primary}
                  onChange={(e) => updateThemeColor('primary', e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="color-picker-item">
              <label>Secondary Color</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={themeColors.secondary}
                  onChange={(e) => updateThemeColor('secondary', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={themeColors.secondary}
                  onChange={(e) => updateThemeColor('secondary', e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="color-picker-item">
              <label>Accent Color</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={themeColors.accent}
                  onChange={(e) => updateThemeColor('accent', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={themeColors.accent}
                  onChange={(e) => updateThemeColor('accent', e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="color-picker-item">
              <label>Background Color</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={themeColors.background}
                  onChange={(e) => updateThemeColor('background', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={themeColors.background}
                  onChange={(e) => updateThemeColor('background', e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="color-picker-item">
              <label>Header Gradient Start</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={themeColors.headerGradient1}
                  onChange={(e) => updateThemeColor('headerGradient1', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={themeColors.headerGradient1}
                  onChange={(e) => updateThemeColor('headerGradient1', e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="color-picker-item">
              <label>Header Gradient End</label>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={themeColors.headerGradient2}
                  onChange={(e) => updateThemeColor('headerGradient2', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={themeColors.headerGradient2}
                  onChange={(e) => updateThemeColor('headerGradient2', e.target.value)}
                  className="color-input"
                />
              </div>
            </div>
          </div>

          <div className="theme-preview">
            <h4>Preview</h4>
            <div 
              className="preview-header"
              style={{
                background: `linear-gradient(135deg, ${themeColors.headerGradient1} 0%, ${themeColors.headerGradient2} 100%)`
              }}
            >
              <div className="preview-content">
                <div className="preview-logo" style={{ backgroundColor: themeColors.secondary }}></div>
                <div className="preview-text">
                  <div className="preview-title" style={{ color: themeColors.secondary }}>FlightPath</div>
                  <div className="preview-subtitle">Navigate your projects with precision</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Baseline Settings Section */}
        <div className="settings-section full-width">
          <div className="section-header">
            <Target size={20} />
            <h3>Performance Baselines</h3>
          </div>
          
          <div className="baseline-settings two-column">
            <div className="baseline-item">
              <label>Default Work Week Hours</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={workWeekCapacity}
                  onChange={(e) => saveWorkWeekCapacity(parseInt(e.target.value) || 40)}
                  min="1"
                  max="168"
                  className="baseline-input"
                />
                <span className="baseline-unit">hours/week</span>
              </div>
              <p className="baseline-description">
                This is the default capacity used for utilization calculations when user-specific capacity is not set.
              </p>
            </div>

            <div className="baseline-item">
              <label>Time to Value Target</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={baselineSettings.timeToValue}
                  onChange={(e) => updateBaselineSetting('timeToValue', parseInt(e.target.value) || 30)}
                  min="1"
                  max="365"
                  className="baseline-input"
                />
                <span className="baseline-unit">days</span>
              </div>
              <p className="baseline-description">
                Target number of days from project start to first value delivery.
              </p>
            </div>

            <div className="baseline-item">
              <label>Task Completion Rate Target</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={baselineSettings.taskCompletion}
                  onChange={(e) => updateBaselineSetting('taskCompletion', parseInt(e.target.value) || 85)}
                  min="1"
                  max="100"
                  className="baseline-input"
                />
                <span className="baseline-unit">%</span>
              </div>
              <p className="baseline-description">
                Target percentage of tasks completed on time.
              </p>
            </div>

            <div className="baseline-item">
              <label>Team Utilization Target</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={baselineSettings.teamUtilization}
                  onChange={(e) => updateBaselineSetting('teamUtilization', parseInt(e.target.value) || 80)}
                  min="1"
                  max="100"
                  className="baseline-input"
                />
                <span className="baseline-unit">%</span>
              </div>
              <p className="baseline-description">
                Target team utilization percentage for optimal productivity.
              </p>
            </div>

            <div className="baseline-item">
              <label>Billable Time Target</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={baselineSettings.billableTimeTarget}
                  onChange={(e) => updateBaselineSetting('billableTimeTarget', parseInt(e.target.value) || 75)}
                  min="1"
                  max="100"
                  className="baseline-input"
                />
                <span className="baseline-unit">%</span>
              </div>
              <p className="baseline-description">
                Target percentage of time that should be billable.
              </p>
            </div>

            <div className="baseline-item">
              <label>Project Success Rate Target</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={baselineSettings.projectSuccessRate}
                  onChange={(e) => updateBaselineSetting('projectSuccessRate', parseInt(e.target.value) || 90)}
                  min="1"
                  max="100"
                  className="baseline-input"
                />
                <span className="baseline-unit">%</span>
              </div>
              <p className="baseline-description">
                Target percentage of projects completed successfully.
              </p>
            </div>

            <div className="baseline-item">
              <label>Utilization Threshold</label>
              <div className="baseline-input-wrapper">
                <input
                  type="number"
                  value={baselineSettings.utilizationThreshold}
                  onChange={(e) => updateBaselineSetting('utilizationThreshold', parseInt(e.target.value) || 10)}
                  min="0"
                  max="50"
                  className="baseline-input"
                />
                <span className="baseline-unit">%</span>
              </div>
              <p className="baseline-description">
                Users with utilization below this threshold will be excluded from the average calculation (e.g., admins who don't track time).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
